import { useState, useEffect, useCallback } from "react";
import { useGoogleLogin } from "@react-oauth/google";

const GMAIL_API = "https://gmail.googleapis.com/gmail/v1/users/me";
const TOKEN_KEY = "gm_access_token";
const TOKEN_EXP_KEY = "gm_token_expiry";
const PROFILE_KEY = "gm_profile";
const THEME_KEY = "gm_theme";

const SCOPES = [
    "https://www.googleapis.com/auth/gmail.readonly",
    "https://www.googleapis.com/auth/gmail.send",
    "https://www.googleapis.com/auth/gmail.modify",
].join(" ");

// ─── helpers ────────────────────────────────────────────────────────────────

function getHeader(headers = [], name) {
    return headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || "";
}

function decodeBase64(str) {
    try {
        return decodeURIComponent(escape(atob(str.replace(/-/g, "+").replace(/_/g, "/"))));
    } catch { return ""; }
}
function getBody(payload) {
    if (!payload) return { type: "text", content: "" };

    // text/plain muna
    if (payload.mimeType === "text/plain" && payload.body?.data)
        return { type: "text", content: decodeBase64(payload.body.data) };

    // text/html
    if (payload.mimeType === "text/html" && payload.body?.data)
        return { type: "html", content: decodeBase64(payload.body.data) };

    if (payload.parts) {
        let htmlFallback = null;
        for (const part of payload.parts) {
            if (part.mimeType === "text/plain" && part.body?.data)
                return { type: "text", content: decodeBase64(part.body.data) };
            if (part.mimeType === "text/html" && part.body?.data)
                htmlFallback = { type: "html", content: decodeBase64(part.body.data) };
        }
        if (htmlFallback) return htmlFallback;
        for (const part of payload.parts) {
            const found = getBody(part);
            if (found.content) return found;
        }
    }
    return { type: "text", content: "" };
}
function initials(name) {
    return name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
}

function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000 && d.getDate() === now.getDate())
        return d.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
    if (diff < 604800000)
        return d.toLocaleDateString("en-PH", { weekday: "short" });
    return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

function buildRaw(to, subject, body, fromEmail) {
    const msg = [`From: ${fromEmail}`, `To: ${to}`, `Subject: ${subject}`, `Content-Type: text/plain; charset=utf-8`, ``, body].join("\r\n");
    return btoa(unescape(encodeURIComponent(msg))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const AVATAR_COLORS = [
    ["#1e3a5f", "#4a90d9"], ["#1e3a2f", "#4a9d6f"],
    ["#3a1e1e", "#d96a4a"], ["#2d1e3a", "#9b6ad9"],
    ["#3a331e", "#d9b84a"], ["#1e383a", "#4ac5d9"],
];

const AVATAR_COLORS_LIGHT = [
    ["#dbeafe", "#1d4ed8"], ["#dcfce7", "#15803d"],
    ["#fee2e2", "#b91c1c"], ["#ede9fe", "#7c3aed"],
    ["#fef9c3", "#a16207"], ["#cffafe", "#0e7490"],
];

function avatarColor(str, isDark) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const colors = isDark ? AVATAR_COLORS : AVATAR_COLORS_LIGHT;
    return colors[Math.abs(hash) % colors.length];
}

// ─── SVG Icons ───────────────────────────────────────────────────────────────

const Icons = {
    Mail: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <polyline points="2,4 12,13 22,4" />
        </svg>
    ),
    Inbox: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
            <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
        </svg>
    ),
    Send: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
    ),
    Star: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
    ),
    Compose: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
    ),
    Search: () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    ),
    Refresh: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
        </svg>
    ),
    Back: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
        </svg>
    ),
    Reply: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 17 4 12 9 7" />
            <path d="M20 18v-2a4 4 0 00-4-4H4" />
        </svg>
    ),
    Close: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    Trash: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
        </svg>
    ),
    Sun: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
    ),
    Moon: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
    ),
    Logout: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
    ),
    Google: () => (
        <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
    ),
};

// ─── CSS ─────────────────────────────────────────────────────────────────────

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

.gm-root *, .gm-root *::before, .gm-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

.gm-root.dark {
  --bg:       #13161c;
  --bg2:      #1a1f28;
  --bg3:      #212733;
  --bg4:      #2a3141;
  --border:   rgba(255,255,255,0.07);
  --border2:  rgba(255,255,255,0.12);
  --text:     #e8ecf4;
  --text2:    #8892a4;
  --text3:    #525e72;
  --accent:   #3b7eff;
  --accent2:  #2563eb;
  --accent-bg: rgba(59,126,255,0.12);
  --unread-bg: #181e2a;
  --unread-dot: #3b7eff;
  --shadow:   0 2px 12px rgba(0,0,0,0.4);
  --shadow2:  0 4px 24px rgba(0,0,0,0.5);
  --badge-bg: #3b7eff;
  --badge-text: #fff;
  --hover-bg: rgba(255,255,255,0.04);
  --tag-bg: rgba(59,126,255,0.15);
  --tag-text: #7aabff;
}

.gm-root.light {
  --bg:       #f0f2f7;
  --bg2:      #ffffff;
  --bg3:      #f5f7fc;
  --bg4:      #eaecf4;
  --border:   rgba(0,0,0,0.07);
  --border2:  rgba(0,0,0,0.12);
  --text:     #1a2033;
  --text2:    #5a6478;
  --text3:    #9aa0b0;
  --accent:   #2563eb;
  --accent2:  #1d4ed8;
  --accent-bg: rgba(37,99,235,0.08);
  --unread-bg: #eef2ff;
  --unread-dot: #2563eb;
  --shadow:   0 2px 12px rgba(0,0,0,0.08);
  --shadow2:  0 4px 24px rgba(0,0,0,0.12);
  --badge-bg: #2563eb;
  --badge-text: #fff;
  --hover-bg: rgba(0,0,0,0.03);
  --tag-bg: rgba(37,99,235,0.1);
  --tag-text: #1d4ed8;
}

.gm-root {
  font-family: 'Inter', sans-serif;
  background: var(--bg);
  color: var(--text);
  height: 100%;
  min-height: 0;

  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: background 0.2s, color 0.2s;
}

.gm-login {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: var(--bg);
}
.gm-login-card {
  background: var(--bg2);
  border: 1px solid var(--border2);
  border-radius: 20px;
  padding: 48px 52px;
  display: flex; flex-direction: column; align-items: center;
  gap: 24px; min-width: 340px;
  box-shadow: var(--shadow2);
}
.gm-login-logo {
  width: 60px; height: 60px;
  background: var(--accent);
  border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  color: #fff;
  box-shadow: 0 4px 16px rgba(59,126,255,0.35);
}
.gm-login-title { font-size: 22px; font-weight: 700; color: var(--text); letter-spacing: -0.3px; }
.gm-login-sub { font-size: 13.5px; color: var(--text2); text-align: center; line-height: 1.65; }
.gm-login-divider { width: 100%; height: 1px; background: var(--border2); }
.gm-login-btn {
  width: 100%; padding: 12px 24px;
  background: var(--bg3);
  color: var(--text);
  border: 1px solid var(--border2);
  border-radius: 12px;
  font-size: 14px; font-weight: 600;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  transition: all 0.15s;
  font-family: 'Inter', sans-serif;
}
.gm-login-btn:hover {
  background: var(--bg4);
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-bg);
}
.gm-login-note { font-size: 11.5px; color: var(--text3); text-align: center; line-height: 1.6; }

.gm-layout { flex: 1; display: flex; overflow: hidden; }

.gm-sidebar {
  width: 230px;
  border-right: 1px solid var(--border);
  background: var(--bg2);
  display: flex; flex-direction: column;
  padding: 20px 14px;
  gap: 2px;
  flex-shrink: 0;
  box-shadow: var(--shadow);
}
.gm-sidebar-brand {
  display: flex; align-items: center; gap: 10px;
  padding: 4px 10px 20px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 8px;
}
.gm-sidebar-brand-icon {
  width: 30px; height: 30px;
  background: var(--accent);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: #fff;
}
.gm-sidebar-brand-name {
  font-size: 15px; font-weight: 700; color: var(--text);
  letter-spacing: -0.2px;
}
.gm-compose-btn {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px;
  background: var(--accent);
  color: #fff;
  border: none; border-radius: 10px;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  margin-bottom: 10px;
  transition: background 0.15s, box-shadow 0.15s;
  font-family: 'Inter', sans-serif;
  box-shadow: 0 2px 8px rgba(59,126,255,0.3);
}
.gm-compose-btn:hover { background: var(--accent2); box-shadow: 0 4px 14px rgba(59,126,255,0.4); }

.gm-nav-section { font-size: 10px; font-weight: 600; color: var(--text3); letter-spacing: 0.8px; text-transform: uppercase; padding: 8px 10px 4px; }

.gm-nav-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px;
  border-radius: 9px;
  font-size: 13px; color: var(--text2);
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  position: relative;
}
.gm-nav-item:hover { background: var(--hover-bg); color: var(--text); }
.gm-nav-item.active { background: var(--accent-bg); color: var(--accent); font-weight: 600; }
.gm-nav-item.active svg { stroke: var(--accent); }
.gm-nav-badge {
  margin-left: auto;
  background: var(--badge-bg); color: var(--badge-text);
  font-size: 10px; font-weight: 700;
  padding: 2px 7px; border-radius: 20px;
  min-width: 20px; text-align: center;
  font-family: 'JetBrains Mono', monospace;
}
.gm-sidebar-footer {
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid var(--border);
  display: flex; flex-direction: column; gap: 2px;
}
.gm-user-row {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: 9px; cursor: pointer;
  transition: background 0.1s;
}
.gm-user-row:hover { background: var(--hover-bg); }
.gm-user-avatar {
  width: 30px; height: 30px; border-radius: 8px;
  background: var(--accent); color: #fff;
  font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  font-family: 'JetBrains Mono', monospace;
}
.gm-user-info { flex: 1; min-width: 0; }
.gm-user-name { font-size: 12px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gm-user-role { font-size: 10.5px; color: var(--text3); }
.gm-theme-btn {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 12px; border-radius: 9px;
  background: none; border: none;
  font-size: 13px; color: var(--text2);
  cursor: pointer; font-family: 'Inter', sans-serif;
  transition: background 0.1s, color 0.1s; width: 100%; text-align: left;
}
.gm-theme-btn:hover { background: var(--hover-bg); color: var(--text); }
.gm-logout-btn {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 12px; border-radius: 9px;
  background: none; border: none;
  font-size: 13px; color: var(--text2);
  cursor: pointer; font-family: 'Inter', sans-serif;
  transition: background 0.1s, color 0.1s; width: 100%; text-align: left;
}
.gm-logout-btn:hover { background: rgba(239,68,68,0.08); color: #ef4444; }

.gm-main { 
  flex: 1; 
  display: flex; 
  flex-direction: column; 
  overflow: hidden; 
  position: relative;  /* ← importante ito */
  background: var(--bg); 
}
.gm-toolbar {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--bg2);
}
.gm-toolbar-title { font-size: 16px; font-weight: 700; color: var(--text); letter-spacing: -0.2px; }
.gm-toolbar-right { display: flex; align-items: center; gap: 6px; margin-left: auto; }
.gm-search-wrap { position: relative; flex: 1; max-width: 380px; }
.gm-search {
  width: 100%;
  background: var(--bg3);
  border: 1px solid var(--border2);
  border-radius: 9px;
  padding: 8px 14px 8px 36px;
  font-size: 13px; color: var(--text);
  outline: none; font-family: 'Inter', sans-serif;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.gm-search::placeholder { color: var(--text3); }
.gm-search:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-bg); }
.gm-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--text3); pointer-events: none; }
.gm-icon-btn {
  background: none; border: 1px solid var(--border2);
  color: var(--text2); cursor: pointer;
  padding: 7px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.gm-icon-btn:hover { background: var(--hover-bg); color: var(--text); border-color: var(--border2); }

.gm-list-header {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--bg2);
}
.gm-list-count { font-size: 11.5px; color: var(--text3); font-weight: 500; }
.gm-list-tag {
  font-size: 10.5px; font-weight: 600; color: var(--tag-text);
  background: var(--tag-bg);
  padding: 2px 8px; border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
}

.gm-list { flex: 1; overflow-y: auto; }
.gm-list::-webkit-scrollbar { width: 4px; }
.gm-list::-webkit-scrollbar-track { background: transparent; }
.gm-list::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }

.gm-email-row {
  display: flex; align-items: center; gap: 14px;
  padding: 13px 20px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.1s;
  position: relative;
}
.gm-email-row:hover { background: var(--hover-bg); }
.gm-email-row.unread { background: var(--unread-bg); }
.gm-email-row.unread::after {
  content: '';
  position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
  background: var(--unread-dot);
  border-radius: 0 3px 3px 0;
}
.gm-avatar {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; flex-shrink: 0;
  font-family: 'JetBrains Mono', monospace;
}
.gm-email-content { flex: 1; min-width: 0; }
.gm-email-top { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; }
.gm-email-sender { font-size: 13px; font-weight: 500; color: var(--text2); }
.gm-email-row.unread .gm-email-sender { font-weight: 700; color: var(--text); }
.gm-email-subject { font-size: 12.5px; color: var(--text3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gm-email-row.unread .gm-email-subject { color: var(--text2); }
.gm-email-snippet { font-size: 11.5px; color: var(--text3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.gm-email-time {
  font-size: 11px; color: var(--text3); flex-shrink: 0;
  font-family: 'JetBrains Mono', monospace;
}
.gm-email-row.unread .gm-email-time { color: var(--accent); font-weight: 600; }

.gm-empty {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: var(--text3); gap: 14px; padding: 40px;
}
.gm-empty-icon {
  width: 56px; height: 56px; border-radius: 14px;
  background: var(--bg3); border: 1px solid var(--border2);
  display: flex; align-items: center; justify-content: center;
  color: var(--text3);
}
.gm-empty-text { font-size: 14px; color: var(--text2); font-weight: 500; }
.gm-empty-sub { font-size: 12px; color: var(--text3); }

.gm-loading {
  flex: 1; display: flex; align-items: center; justify-content: center;
  color: var(--text3); gap: 10px; font-size: 13px;
}
.gm-spin { animation: gmSpin 0.8s linear infinite; display: inline-flex; }
@keyframes gmSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }


 
@keyframes gmSlideIn { from { opacity:0; transform: translateX(16px); } to { opacity:1; transform: translateX(0); } }

.gm-panel-header {
  display: flex; align-items: center; gap: 10px;
  padding: 13px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--bg2);
}
.gm-panel-subject {
  font-size: 15px; font-weight: 700; flex: 1; color: var(--text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  letter-spacing: -0.2px;
}
.gm-panel-body { flex: 1; overflow-y: auto; padding: 28px; }
.gm-panel-body::-webkit-scrollbar { width: 4px; }
.gm-panel-body::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }

.gm-meta-card {
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px 20px;
  display: flex; align-items: center; gap: 14px;
  margin-bottom: 24px;
}
.gm-meta-avatar {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-weight: 700; flex-shrink: 0;
  font-family: 'JetBrains Mono', monospace;
}
.gm-meta-name { font-size: 14px; font-weight: 700; color: var(--text); }
.gm-meta-addr { font-size: 12px; color: var(--text3); font-family: 'JetBrains Mono', monospace; margin-top: 2px; }
.gm-meta-date { margin-left: auto; font-size: 11.5px; color: var(--text3); font-family: 'JetBrains Mono', monospace; text-align: right; }
.gm-meta-date-label { font-size: 10px; color: var(--text3); margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.5px; }

.gm-email-body-wrap {
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 24px;
}
.gm-email-body { font-size: 13.5px; color: var(--text2); line-height: 1.9; white-space: pre-wrap; word-break: break-word; }

.gm-panel,
.gm-compose {
  position: absolute; inset: 0;
  background: var(--bg);
  display: flex; flex-direction: column;
  animation: gmSlideIn 0.16s ease;
  z-index: 10;
}
.gm-compose-fields {
  background: var(--bg2);
  border-bottom: 1px solid var(--border);
}
.gm-compose-field {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 20px;
  border-bottom: 1px solid var(--border);
}
.gm-compose-label { font-size: 11.5px; font-weight: 600; color: var(--text3); width: 56px; flex-shrink: 0; text-transform: uppercase; letter-spacing: 0.5px; }
.gm-compose-input {
  flex: 1; background: none; border: none; outline: none;
  font-size: 13.5px; color: var(--text); font-family: 'Inter', sans-serif;
}
.gm-compose-input::placeholder { color: var(--text3); }
.gm-compose-textarea {
  flex: 1; background: var(--bg); border: none; outline: none; resize: none;
  font-size: 13.5px; color: var(--text); font-family: 'Inter', sans-serif;
  line-height: 1.85; padding: 20px;
}
.gm-compose-textarea::placeholder { color: var(--text3); }
.gm-compose-footer {
  padding: 12px 20px;
  border-top: 1px solid var(--border);
  background: var(--bg2);
  display: flex; align-items: center; gap: 10px;
}
.gm-send-btn {
  padding: 9px 22px;
  background: var(--accent); color: #fff;
  border: none; border-radius: 9px;
  font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: 'Inter', sans-serif;
  transition: background 0.15s, box-shadow 0.15s;
  display: flex; align-items: center; gap: 8px;
  box-shadow: 0 2px 8px rgba(59,126,255,0.3);
}
.gm-send-btn:hover { background: var(--accent2); box-shadow: 0 4px 14px rgba(59,126,255,0.4); }
.gm-send-btn:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }
.gm-discard-btn {
  padding: 9px 16px;
  background: none; color: var(--text2);
  border: 1px solid var(--border2); border-radius: 9px;
  font-size: 13px; font-weight: 500;
  cursor: pointer; font-family: 'Inter', sans-serif;
  display: flex; align-items: center; gap: 6px;
  transition: all 0.15s;
}
.gm-discard-btn:hover { background: rgba(239,68,68,0.08); color: #ef4444; border-color: rgba(239,68,68,0.2); }

.gm-toast {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  background: var(--bg2); border: 1px solid var(--border2); border-radius: 12px;
  padding: 12px 20px; font-size: 13px; color: var(--text);
  z-index: 100; animation: gmToastIn 0.2s ease;
  display: flex; align-items: center; gap: 10px;
  box-shadow: var(--shadow2);
  font-weight: 500;
}

.gm-sidebar-close, .gm-hamburger { display: none; }

@media (max-width: 640px) {
  .gm-hamburger {
    display: flex; align-items: center; justify-content: center;
    background: none; border: 1px solid var(--border2);
    color: var(--text2); cursor: pointer;
    padding: 7px; border-radius: 8px; flex-shrink: 0;
  }
  .gm-sidebar {
    position: fixed; left: -100%; top: 0; bottom: 0;
    z-index: 50; width: 260px;
    transition: left 0.25s ease;
    box-shadow: var(--shadow2);
  }
  .gm-sidebar.open { left: 0; }
  .gm-sidebar-close {
    display: flex; margin-left: auto;
    background: none; border: none;
    color: var(--text2); cursor: pointer;
    padding: 4px; border-radius: 6px;
  }
  .gm-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.45); z-index: 49;
  }
}
.gm-toast-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
@keyframes gmToastIn { from { opacity:0; transform: translateX(-50%) translateY(10px); } to { opacity:1; transform: translateX(-50%) translateY(0); } }
`;

let cssInjected = false;
function injectCSS() {
    if (cssInjected) return;
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);
    cssInjected = true;
}

// ─── Token persistence ────────────────────────────────────────────────────────

function saveToken(accessToken, expiresIn = 3600) {
    const expiry = Date.now() + expiresIn * 1000;
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(TOKEN_EXP_KEY, String(expiry));
}

function loadToken() {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiry = Number(localStorage.getItem(TOKEN_EXP_KEY));
    if (!token || !expiry) return null;
    if (Date.now() >= expiry) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXP_KEY);
        return null;
    }
    return { token, expiry };
}

function clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXP_KEY);
    localStorage.removeItem(PROFILE_KEY);
}

function saveProfile(profile) {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

function loadProfile() {
    try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null"); } catch { return null; }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Gmail() {
    injectCSS();

    const savedTheme = localStorage.getItem(THEME_KEY) || "dark";
    const [theme, setTheme] = useState(savedTheme);

    // NEW: On reload, loadToken() automatically restores session from localStorage
    // If token is still valid, user stays logged in — no login screen shown
    const savedTokenData = loadToken();
    const [token, setToken] = useState(savedTokenData?.token || null);
    const [tokenExpiry, setTokenExpiry] = useState(savedTokenData?.expiry || null);
    const [profile, setProfile] = useState(loadProfile());
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [composing, setComposing] = useState(false);
    const [search, setSearch] = useState("");
    const [view, setView] = useState("inbox");
    const [toast, setToast] = useState(null);
    const [sending, setSending] = useState(false);
    const [compose, setCompose] = useState({ to: "", subject: "", body: "" });

    const isDark = theme === "dark";

    const toggleTheme = () => {
        const next = isDark ? "light" : "dark";
        setTheme(next);
        localStorage.setItem(THEME_KEY, next);
    };

    const showToast = (msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const gHeaders = useCallback(
        (extra = {}) => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...extra,
        }),
        [token]
    );

    // ── Manual login (popup) ──────────────────────────────────────────────────
    const login = useGoogleLogin({
        scope: SCOPES,
        flow: "implicit",
        ux_mode: "popup",
        onSuccess: async (res) => {
            const accessToken = res.access_token;
            const expiresIn = res.expires_in || 3600;
            saveToken(accessToken, expiresIn);
            setToken(accessToken);
            setTokenExpiry(Date.now() + expiresIn * 1000);

            const p = await fetch("https://www.googleapis.com/oauth2/v1/userinfo", {
                headers: { Authorization: `Bearer ${accessToken}` },
            }).then((r) => r.json());
            setProfile(p);
            saveProfile(p);
        },
        onError: () => showToast("Login failed", "error"),
    });

    // ── NEW: Silent login — no popup, runs in background ─────────────────────
    // As long as user is signed in to Google on their browser, this works
    // silently without any interaction needed
    const silentLogin = useGoogleLogin({
        scope: SCOPES,
        flow: "implicit",
        ux_mode: "popup",
        prompt: "none", // <-- KEY: tells Google to skip the login popup
        onSuccess: (res) => {
            const accessToken = res.access_token;
            const expiresIn = res.expires_in || 3600;
            saveToken(accessToken, expiresIn);
            setToken(accessToken);
            setTokenExpiry(Date.now() + expiresIn * 1000);
            // No need to re-fetch profile — already saved in localStorage
        },
        onError: () => {
            // Silent fail — if this fails, user just continues until token expires
            // then they'll see the login screen naturally
            console.log("Silent re-auth failed — user will need to log in manually");
        },
    });

    // ── NEW: Auto-refresh timer ───────────────────────────────────────────────
    // Checks every 60 seconds if token is about to expire (within 5 minutes)
    // If yes, triggers silent re-auth in the background
    useEffect(() => {
        if (!token || !tokenExpiry) return;

        const interval = setInterval(() => {
            const timeLeft = tokenExpiry - Date.now();
            const fiveMinutes = 5 * 60 * 1000;

            if (timeLeft <= fiveMinutes && timeLeft > 0) {
                silentLogin(); // auto-refresh before expiry
            }
        }, 60 * 1000); // check every 60 seconds

        return () => clearInterval(interval); // cleanup on unmount
    }, [token, tokenExpiry, silentLogin]);

    // ── Logout ────────────────────────────────────────────────────────────────
    const logout = () => {
        clearToken();
        setToken(null);
        setProfile(null);
        setEmails([]);
        setSelected(null);
        setComposing(false);
    };

    const fetchEmails = useCallback(
        async (labelId = "INBOX") => {
            if (!token) return;
            setLoading(true);
            try {
                const listRes = await fetch(
                    `${GMAIL_API}/messages?maxResults=25&labelIds=${labelId}`,
                    { headers: gHeaders() }
                ).then((r) => r.json());

                if (!listRes.messages) { setEmails([]); setLoading(false); return; }

                const details = await Promise.all(
                    listRes.messages.map((m) =>
                        fetch(`${GMAIL_API}/messages/${m.id}?format=full`, { headers: gHeaders() }).then((r) => r.json())
                    )
                );
                setEmails(details);
            } catch {
                showToast("Failed to load emails", "error");
            }
            setLoading(false);
        },
        [token, gHeaders]
    );

    useEffect(() => {
        if (!token) return;
        const label = view === "sent" ? "SENT" : view === "starred" ? "STARRED" : "INBOX";
        fetchEmails(label);
    }, [token, view, fetchEmails]);

    const sendEmail = async () => {
        if (!compose.to || !compose.subject || !compose.body) {
            showToast("Fill in all fields", "warning");
            return;
        }
        setSending(true);
        try {
            const raw = buildRaw(compose.to, compose.subject, compose.body, profile?.email || "me");
            await fetch(`${GMAIL_API}/messages/send`, {
                method: "POST",
                headers: gHeaders(),
                body: JSON.stringify({ raw }),
            });
            showToast("Email sent successfully");
            setComposing(false);
            setCompose({ to: "", subject: "", body: "" });
        } catch {
            showToast("Failed to send email", "error");
        }
        setSending(false);
    };

    const markRead = async (id) => {
        await fetch(`${GMAIL_API}/messages/${id}/modify`, {
            method: "POST",
            headers: gHeaders(),
            body: JSON.stringify({ removeLabelIds: ["UNREAD"] }),
        });
        setEmails((prev) =>
            prev.map((e) =>
                e.id === id ? { ...e, labelIds: (e.labelIds || []).filter((l) => l !== "UNREAD") } : e
            )
        );
    };

    const openEmail = (email) => {
        setSelected(email);
        setComposing(false);
        if (email.labelIds?.includes("UNREAD")) markRead(email.id);
    };

    const openCompose = (prefill = {}) => {
        setCompose({ to: prefill.to || "", subject: prefill.subject || "", body: prefill.body || "" });
        setComposing(true);
        setSelected(null);
    };

    const filtered = emails.filter((e) => {
        if (!search) return true;
        const headers = e.payload?.headers || [];
        const from = getHeader(headers, "From").toLowerCase();
        const subj = getHeader(headers, "Subject").toLowerCase();
        const s = search.toLowerCase();
        return from.includes(s) || subj.includes(s);
    });

    const unreadCount = emails.filter((e) => e.labelIds?.includes("UNREAD")).length;
    const toastColors = { success: "#22c55e", error: "#ef4444", warning: "#f59e0b" };
    const viewLabels = { inbox: "Inbox", sent: "Sent", starred: "Starred" };

    // ── Login screen ──────────────────────────────────────────────────────────
    // NEW: This only shows if localStorage has no valid token
    // Pag-reload with valid token = skips this screen entirely
    if (!token) {
        return (
            <div className={`gm-root ${theme}`}>
                <div className="gm-login">
                    <div className="gm-login-card">
                        <div className="gm-login-logo"><Icons.Mail /></div>
                        <div style={{ textAlign: "center" }}>
                            <div className="gm-login-title">Mail</div>
                            <div className="gm-login-sub" style={{ marginTop: 6 }}>
                                Sign in with your Google account to<br />access your inbox and send emails.
                            </div>
                        </div>
                        <div className="gm-login-divider" />
                        <button className="gm-login-btn" onClick={() => login()}>
                            <Icons.Google />
                            Continue with Google
                        </button>
                        <div className="gm-login-note">
                            Your session is kept alive automatically.<br />
                            Sign out anytime using the sidebar button.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── App ───────────────────────────────────────────────────────────────────
    return (
        <div className={`gm-root ${theme}`}>
            <div className="gm-layout">
                {sidebarOpen && <div className="gm-overlay" onClick={() => setSidebarOpen(false)} />}

                {/* Sidebar */}
                <div className={`gm-sidebar${sidebarOpen ? " open" : ""}`}>
                    <div className="gm-sidebar-brand">
                        <div className="gm-sidebar-brand-icon"><Icons.Mail /></div>
                        <div className="gm-sidebar-brand-name">Mail</div>
                        <button className="gm-sidebar-close" onClick={() => setSidebarOpen(false)}><Icons.Close /></button>
                    </div>
                    <button className="gm-compose-btn" onClick={() => { openCompose(); setSidebarOpen(false); }}>
                        <Icons.Compose /> Compose
                    </button>
                    <div className="gm-nav-section">Mailbox</div>
                    {[
                        { id: "inbox", icon: <Icons.Inbox />, label: "Inbox", badge: unreadCount },
                        { id: "sent", icon: <Icons.Send />, label: "Sent" },
                        { id: "starred", icon: <Icons.Star />, label: "Starred" },
                    ].map(({ id, icon, label, badge }) => (
                        <div key={id}
                            className={`gm-nav-item${view === id ? " active" : ""}`}
                            onClick={() => { setView(id); setSelected(null); setComposing(false); setSidebarOpen(false); }}>
                            {icon}{label}
                            {badge > 0 && <span className="gm-nav-badge">{badge}</span>}
                        </div>
                    ))}
                    <div className="gm-sidebar-footer">
                        <div className="gm-user-row">
                            <div className="gm-user-avatar">
                                {profile?.picture
                                    ? <img src={profile.picture} alt="" style={{ width: "100%", height: "100%", borderRadius: 8, objectFit: "cover" }} />
                                    : initials(profile?.name || "U")}
                            </div>
                            <div className="gm-user-info">
                                <div className="gm-user-name">{profile?.name || profile?.email}</div>
                                <div className="gm-user-role">{profile?.email}</div>
                            </div>
                        </div>
                        <button className="gm-theme-btn" onClick={toggleTheme}>
                            {isDark ? <Icons.Sun /> : <Icons.Moon />}
                            {isDark ? "Light Mode" : "Dark Mode"}
                        </button>
                        <button className="gm-logout-btn" onClick={logout}>
                            <Icons.Logout /> Sign Out
                        </button>
                    </div>
                </div>

                {/* Main */}
                <div className="gm-main">
                    {/* NEW: Removed expiry warning bar — no longer needed since auto-refresh handles it */}
                    <div className="gm-toolbar">

                        <button className="gm-hamburger" onClick={() => setSidebarOpen(true)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="3" y1="6" x2="21" y2="6" />
                                <line x1="3" y1="12" x2="21" y2="12" />
                                <line x1="3" y1="18" x2="21" y2="18" />
                            </svg>
                        </button>
                        <div className="gm-toolbar-title">{viewLabels[view]}</div>

                        <div className="gm-search-wrap">
                            <span className="gm-search-icon"><Icons.Search /></span>
                            <input className="gm-search" placeholder="Search emails..."
                                value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <div className="gm-toolbar-right">
                            <button className="gm-icon-btn"
                                onClick={() => fetchEmails(view === "sent" ? "SENT" : view === "starred" ? "STARRED" : "INBOX")}
                                title="Refresh">
                                <Icons.Refresh />
                            </button>
                        </div>
                    </div>
                    {!loading && filtered.length > 0 && (
                        <div className="gm-list-header">
                            <span className="gm-list-count">{filtered.length} messages</span>
                            {unreadCount > 0 && view === "inbox" && (
                                <span className="gm-list-tag">{unreadCount} unread</span>
                            )}
                        </div>
                    )}
                    {loading ? (
                        <div className="gm-loading">
                            <span className="gm-spin"><Icons.Refresh /></span>
                            Loading messages...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="gm-empty">
                            <div className="gm-empty-icon"><Icons.Inbox /></div>
                            <div className="gm-empty-text">No messages found</div>
                            <div className="gm-empty-sub">
                                {search ? `No results for "${search}"` : `Your ${view} is empty`}
                            </div>
                        </div>
                    ) : (
                        <div className="gm-list">
                            {filtered.map((email) => {
                                const headers = email.payload?.headers || [];
                                const from = getHeader(headers, "From");
                                const subject = getHeader(headers, "Subject") || "(no subject)";
                                const date = getHeader(headers, "Date");
                                const snippet = email.snippet || "";
                                const unread = email.labelIds?.includes("UNREAD");
                                const name = from.replace(/<.*>/, "").trim() || from;
                                const [bg, fg] = avatarColor(from, isDark);
                                return (
                                    <div key={email.id}
                                        className={`gm-email-row${unread ? " unread" : ""}`}
                                        onClick={() => openEmail(email)}>
                                        <div className="gm-avatar" style={{ background: bg, color: fg }}>
                                            {initials(name || "?")}
                                        </div>
                                        <div className="gm-email-content">
                                            <div className="gm-email-top">
                                                <div className="gm-email-sender">{name}</div>
                                            </div>
                                            <div className="gm-email-subject">{subject}</div>
                                            {snippet && <div className="gm-email-snippet">{snippet}</div>}
                                        </div>
                                        <div className="gm-email-time">{formatDate(date)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {selected && (() => {
                        const headers = selected.payload?.headers || [];
                        const from = getHeader(headers, "From");
                        const subject = getHeader(headers, "Subject") || "(no subject)";
                        const date = getHeader(headers, "Date");
                        const bodyData = getBody(selected.payload);
                        const name = from.replace(/<.*>/, "").trim() || from;
                        const addr = from.match(/<(.+)>/)?.[1] || from;
                        const [bg, fg] = avatarColor(from, isDark);
                        return (
                            <div className="gm-panel">
                                <div className="gm-panel-header">
                                    <button className="gm-icon-btn" onClick={() => setSelected(null)}><Icons.Back /></button>
                                    <div className="gm-panel-subject">{subject}</div>
                                    <button className="gm-icon-btn" title="Reply"
                                        onClick={() => openCompose({ to: addr, subject: `Re: ${subject}`, body: `\n\n--- Original message ---\nFrom: ${from}\n${bodyData.content}` })}>
                                        <Icons.Reply />
                                    </button>
                                </div>
                                <div className="gm-panel-body">
                                    <div className="gm-meta-card">
                                        <div className="gm-meta-avatar" style={{ background: bg, color: fg }}>{initials(name)}</div>
                                        <div>
                                            <div className="gm-meta-name">{name}</div>
                                            <div className="gm-meta-addr">{addr}</div>
                                        </div>
                                        <div className="gm-meta-date">
                                            <div className="gm-meta-date-label">Received</div>
                                            {new Date(date).toLocaleString("en-PH")}
                                        </div>
                                    </div>
                                    <div className="gm-email-body-wrap">
                                        {bodyData.type === "html" ? (
                                            <iframe
                                                srcDoc={bodyData.content}
                                                style={{ width: "100%", minHeight: "400px", border: "none", borderRadius: 8 }}
                                                sandbox="allow-same-origin"
                                                title="email-body"
                                            />
                                        ) : (
                                            <div className="gm-email-body">{bodyData.content || selected.snippet || "(No content)"}</div>
                                        )}                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {composing && (
                        <div className="gm-compose">
                            <div className="gm-panel-header">
                                <button className="gm-icon-btn" onClick={() => setComposing(false)}><Icons.Close /></button>
                                <div className="gm-panel-subject">New Message</div>
                            </div>
                            <div className="gm-compose-fields">
                                <div className="gm-compose-field">
                                    <span className="gm-compose-label">To</span>
                                    <input className="gm-compose-input" placeholder="recipient@email.com"
                                        value={compose.to} onChange={(e) => setCompose((p) => ({ ...p, to: e.target.value }))} />
                                </div>
                                <div className="gm-compose-field">
                                    <span className="gm-compose-label">Subject</span>
                                    <input className="gm-compose-input" placeholder="Enter subject..."
                                        value={compose.subject} onChange={(e) => setCompose((p) => ({ ...p, subject: e.target.value }))} />
                                </div>
                            </div>
                            <textarea className="gm-compose-textarea" placeholder="Write your message here..."
                                value={compose.body} onChange={(e) => setCompose((p) => ({ ...p, body: e.target.value }))} />
                            <div className="gm-compose-footer">
                                <button className="gm-send-btn" onClick={sendEmail} disabled={sending}>
                                    <Icons.Send />
                                    {sending ? "Sending..." : "Send"}
                                </button>
                                <button className="gm-discard-btn" onClick={() => setComposing(false)}>
                                    <Icons.Trash /> Discard
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {toast && (
                <div className="gm-toast">
                    <div className="gm-toast-dot" style={{ background: toastColors[toast.type] || toastColors.success }} />
                    {toast.msg}
                </div>
            )}
        </div>
    );
}